// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import Chip from '@mui/material/Chip'
import { CheckCircle2, Clock3, AlertTriangle, CircleDot, Minus } from 'lucide-react'
import { statusTone, type StatusTone } from '../../api/status'
import { labelOf, type LabelDomain } from '../../ui/statusLabels'

/**
 * D-UI-3 c5 — one status pill for the whole app (replaces 7+ hand-rolled palettes; audit C19, O21).
 * Colour comes from the shared status dictionary (api/status.ts — "a colour means the same thing on every
 * screen"); the words from statusLabels.ts. Every tone also carries an icon, so the state is never colour-only.
 */
const TONE_STYLE: Record<StatusTone, { fg: string; bg: string; Icon: typeof CheckCircle2; name: string }> = {
  green: { fg: '#15803d', bg: '#ecfdf3', Icon: CheckCircle2, name: 'done' },
  amber: { fg: '#a16207', bg: '#fef9e7', Icon: Clock3, name: 'waiting' },
  red: { fg: '#b91c1c', bg: '#fef2f2', Icon: AlertTriangle, name: 'problem' },
  blue: { fg: '#0f6e64', bg: '#eef8f6', Icon: CircleDot, name: 'in progress' },
  grey: { fg: '#57534e', bg: '#f5f5f4', Icon: Minus, name: 'neutral' },
}

export interface StatusChipProps {
  status: string | null | undefined
  domain?: LabelDomain
  /** Override the words (the colour still follows `status`). */
  label?: string
  /** Override the colour when a domain's meaning differs from the shared dictionary. */
  tone?: StatusTone
}

export default function StatusChip({ status, domain = 'status', label, tone }: StatusChipProps) {
  const t = TONE_STYLE[tone ?? statusTone(status)]
  const text = label ?? labelOf(status, domain)
  return (
    <Chip size="small" icon={<t.Icon size={13} aria-hidden="true" />} label={text}
      data-tone={t.name}
      sx={{ color: t.fg, bgcolor: t.bg, border: `1px solid ${t.fg}33`, height: 22, fontSize: 11.5,
            '& .MuiChip-icon': { color: t.fg, ml: '6px', mr: '-2px' } }} />
  )
}
